import { Link } from 'react-router-dom';
import '../styles/home-projects.css';
import Slider from 'react-slick';
import {
  FaArrowRight,
  FaBolt,
  FaExternalLinkAlt,
  FaLayerGroup,
  FaRegCompass,
  FaRocket,
} from 'react-icons/fa';
import {
  HOME_PROJECTS,
  PROOF_CHIPS,
  PROJECT_CATEGORIES,
} from '../data/homeProjects';
import { usePrefersReducedMotion } from '../hooks/usePrefersReducedMotion';
import { slickDotPaging } from '../utils/slickAccessibleDots';

function ProjectLink({ project, className, children }) {
  return (
    <a
      href={project.href}
      target="_blank"
      rel="noreferrer"
      className={className}
      aria-label={project.label}
    >
      {children}
    </a>
  );
}

function ProjectMedia({ project, width = 480, height = 280 }) {
  if (project.image) {
    return <img src={project.image} alt={project.alt} loading="lazy" width={width} height={height} />;
  }

  return (
    <div className="home-projects-showcase__fallback-media" role="img" aria-label={project.alt}>
      <span>{project.fallbackKicker || project.category}</span>
      <strong>{project.fallbackText || project.title}</strong>
    </div>
  );
}

function ServiceChips({ services }) {
  return (
    <ul className="home-projects-showcase__chips" aria-label="Technologies and deliverables">
      {services.map((service) => (
        <li key={service}>{service}</li>
      ))}
    </ul>
  );
}

function ProjectSliderArrow({
  className,
  onClick,
  direction,
}: {
  className?: string;
  onClick?: () => void;
  direction: string;
}) {
  const next = direction === 'next';
  return (
    <button
      type="button"
      className={`${className || ''} home-projects-showcase__slider-arrow home-projects-showcase__slider-arrow--${direction}`}
      onClick={onClick}
      aria-label={`${next ? 'Next' : 'Previous'} project slide`}
    >
      {/* Explicit px size — slick sets font-size:0, so react-icons 1em icons collapse to 0×0 */}
      <svg width="18" height="18" viewBox="0 0 24 24" aria-hidden="true" focusable="false">
        {next ? (
          <path
            fill="currentColor"
            d="M8.59 16.59L13.17 12 8.59 7.41 10 6l6 6-6 6-1.41-1.41z"
          />
        ) : (
          <path
            fill="currentColor"
            d="M15.41 16.59L10.83 12l4.58-4.59L14 6l-6 6 6 6 1.41-1.41z"
          />
        )}
      </svg>
    </button>
  );
}

const PROOF_STATS = [
  { value: '10+', label: 'production snapshots' },
  { value: '5', label: 'service categories' },
  { value: '360°', label: 'design + engineering' },
];

const Ourproject = () => {
  const reduceMotion = usePrefersReducedMotion();
  const projectSliderSettings = {
    dots: true,
    customPaging: slickDotPaging('Go to project'),
    arrows: true,
    infinite: true,
    speed: 520,
    slidesToShow: 1,
    slidesToScroll: 1,
    autoplay: !reduceMotion,
    autoplaySpeed: 5200,
    pauseOnHover: true,
    nextArrow: <ProjectSliderArrow direction="next" />,
    prevArrow: <ProjectSliderArrow direction="prev" />,
    responsive: [{ breakpoint: 640, settings: { arrows: false } }],
  };

  return (
    <section className="home-projects-showcase home-surface--light" aria-labelledby="home-projects-heading">
      <div className="home-projects-showcase__ambient home-projects-showcase__ambient--one" aria-hidden="true" />
      <div className="home-projects-showcase__ambient home-projects-showcase__ambient--two" aria-hidden="true" />
      <div className="home-projects-showcase__shell">
        <div className="home-projects-showcase__header">
          <div className="home-projects-showcase__header-copy">
            <p className="home-projects-showcase__eyebrow">
              <FaRegCompass aria-hidden="true" /> Proof studio
            </p>
            <h2 id="home-projects-heading" className="home-projects-showcase__title">
              Selected work presented like a product lab
            </h2>
            <p className="home-projects-showcase__lead">
              A curated look at websites, ecommerce systems, AI interfaces, and operational platforms we have
              designed, developed, integrated, and supported.
            </p>
            <div className="home-projects-showcase__stats" aria-label="Portfolio proof metrics">
              {PROOF_STATS.map((stat) => (
                <span key={stat.label}>
                  <strong>{stat.value}</strong>
                  {stat.label}
                </span>
              ))}
            </div>
          </div>
          <div className="home-projects-showcase__proof-panel" aria-label="Project categories we deliver">
            <div className="home-projects-showcase__proof-panel-top">
              <span><FaLayerGroup aria-hidden="true" /> Delivery mix</span>
              <FaBolt aria-hidden="true" />
            </div>
            <div className="home-projects-showcase__proof-chips">
              {PROOF_CHIPS.map((chip) => (
                <span key={chip}>{chip}</span>
              ))}
            </div>
          </div>
        </div>

        <div
          className="home-projects-showcase__filters"
          role="list"
          aria-label="Project categories in this showcase"
        >
          {PROJECT_CATEGORIES.map((cat) => (
            <span key={cat.id} className="home-projects-showcase__filter" role="listitem">
              {cat.label}
            </span>
          ))}
        </div>

        <div className="home-projects-showcase__slider-section home-projects-showcase__showreel" aria-label="Portfolio project slider">
          <div className="home-projects-showcase__slider-heading">
            <div>
              <p className="home-projects-showcase__eyebrow">
                <FaRocket aria-hidden="true" /> Project showreel
              </p>
              <h3>One focused portfolio slider for platforms, ecommerce, AI, and service work</h3>
            </div>
            <Link to="/portfolio" className="home-projects-showcase__project-cta">
              Open portfolio <FaArrowRight aria-hidden="true" />
            </Link>
          </div>

          <Slider {...projectSliderSettings} className="home-projects-showcase__slider home-projects-showcase__showreel-slider">
            {HOME_PROJECTS.map((project, index) => (
              <div key={project.id} className="home-projects-showcase__showreel-slide">
                <article className="home-projects-showcase__showreel-card">
                  <div className="home-projects-showcase__showreel-media">
                    <div className="home-projects-showcase__showreel-browser" aria-hidden="true">
                      <span />
                      <span />
                      <span />
                      <strong>{project.category}</strong>
                    </div>
                    <ProjectMedia project={project} width={860} height={540} />
                    <span className="home-projects-showcase__showreel-count">
                      {String(index + 1).padStart(2, '0')} / {String(HOME_PROJECTS.length).padStart(2, '0')}
                    </span>
                  </div>
                  <div className="home-projects-showcase__showreel-copy">
                    <span className="home-projects-showcase__showreel-kicker">Case study preview</span>
                    <span className="home-projects-showcase__badge">{project.category}</span>
                    <h3>{project.title}</h3>
                    <p>{project.summary}</p>
                    <ServiceChips services={project.services} />
                    <div className="home-projects-showcase__showreel-actions">
                      <ProjectLink project={project} className="home-projects-showcase__project-cta">
                        View project <FaExternalLinkAlt aria-hidden="true" />
                      </ProjectLink>
                      <span className="home-projects-showcase__showreel-note">External project snapshot</span>
                    </div>
                  </div>
                </article>
              </div>
            ))}
          </Slider>
        </div>

        <div className="home-projects-showcase__footer-meta" aria-hidden="true">
          <span>{HOME_PROJECTS.length} production snapshots</span>
          <span>Magento · Laravel · React · PHP · WordPress</span>
        </div>

        <div className="home-projects-showcase__cta-band">
          <div>
            <h3>Want a similar result?</h3>
            <p>Tell us what you are building—we will recommend the right mix of design, engineering, and support.</p>
          </div>
          <nav className="home-post-hero__actions" aria-label="Project section actions">
            <Link to="/getintouch" className="home-post-hero__btn home-post-hero__btn--primary">
              Start a project
            </Link>
            <Link to="/portfolio" className="home-post-hero__btn home-post-hero__btn--secondary">
              View full portfolio
            </Link>
          </nav>
        </div>
      </div>
    </section>
  );
};

export default Ourproject;
